/**
 * MY PreSet (`PreSetIncorporationPanel` 등) 과 동일 패턴:
 * - `/api/aiDocuments/incorporation/prepare-documents` body 의 documents[] 에
 *   `formData`(→ L_DOC_CUST_SAVE.DATA_JSON) + 선택적 `signers`(→ SIGNER_JSON) 전달
 * - 서명자는 `formData.signers` JSON 문자열에서 추출 (템플릿 [[signers]] 바인딩과 일치)
 */

export interface PresetDocCustSigner {
  name: string;
  email: string;
  role: string;
  /** 서명자 1명 가운데 정렬(SignersField "Center signature") — 백엔드 replaceSignerBlocks 가 읽는다 */
  _signCenter?: boolean;
}

/**
 * MY 와 동일: doc.signers 우선 없으면 formData.signers 파싱.
 */
export function extractSignersFromPresetFormData(
  formData: Record<string, any> | null | undefined,
): PresetDocCustSigner[] | undefined {
  if (!formData || typeof formData !== 'object') return undefined;
  const raw = formData.signers ?? formData.Signers;
  if (raw == null || raw === '') return undefined;
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (!Array.isArray(parsed) || parsed.length === 0) return undefined;
    const out: PresetDocCustSigner[] = [];
    for (const x of parsed) {
      if (!x || typeof x !== 'object') continue;
      const name = String((x as any).name || '').trim();
      if (!name) continue;
      out.push({
        name,
        email: String((x as any).email || '').trim(),
        role: String((x as any).role || 'signer'),
        // 이름·이메일·역할만 남기면 화면에서 고른 가운데 정렬이 문서에 안 나간다 → 켜진 경우만 유지
        ...((x as any)._signCenter === true ? { _signCenter: true } : {}),
      });
    }
    return out.length ? out : undefined;
  } catch {
    return undefined;
  }
}

/**
 * DATA_JSON 에 서명자 배열을 문자열로 넣어 템플릿 치환·재조회 시 동일하게 동작하도록 병합.
 */
export function mergeSignersIntoFormDataForStorage(
  formData: Record<string, any>,
  signers: PresetDocCustSigner[] | undefined,
): Record<string, any> {
  const out = { ...formData };
  if (signers?.length) {
    const json = JSON.stringify(signers);
    out.signers = json;
    out.Signers = json;
  }
  return out;
}

const SIGNERS_PICKER_KEY_RE = /^signers?\d*$/i;

function toSignerJsonString(value: unknown): string {
  if (value == null || value === '') return '';
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value);
  } catch {
    return '';
  }
}

/** signers(우선) → Signers 미러 — prepare 이후 Edit·Preview·legacy DATA_JSON 정합용 */
export function mirrorSignersPickerKeys(formData: Record<string, any> | null | undefined): void {
  if (!formData || typeof formData !== 'object') return;
  const raw = formData.signers ?? formData.Signers;
  if (raw == null || raw === '') return;
  const json = toSignerJsonString(raw);
  formData.signers = json;
  formData.Signers = json;
}

/** SignerListField update — signers·Signers 동일 JSON (mapper는 signers만, prepare에서 merge) */
export function applySignersPickerUpdate(
  formData: Record<string, any>,
  varKey: string,
  value: unknown,
): void {
  formData[varKey] = value;
  if (!SIGNERS_PICKER_KEY_RE.test(String(varKey || '').trim())) return;
  const json = toSignerJsonString(value);
  formData.signers = json;
  formData.Signers = json;
}

export interface BuildSgPresetDocPayloadInput {
  templateId: number | string;
  templateName: string;
  personName: string;
  /** toTemplateVariables() 결과 등 */
  commonFormData: Record<string, any>;
  entityCd?: string;
}

/**
 * prepare-documents 한 건분 — MY `documents.map` 과 같은 필드 구성.
 */
export function buildSgPresetPrepareDocumentItem(input: BuildSgPresetDocPayloadInput) {
  const signersOut =
    extractSignersFromPresetFormData(input.commonFormData) ?? undefined;
  const formDataMerged = mergeSignersIntoFormDataForStorage(input.commonFormData, signersOut);
  return {
    templateId: input.templateId,
    label: input.templateName,
    personName: input.personName,
    formData: formDataMerged,
    ...(signersOut?.length ? { signers: signersOut } : {}),
    ...(input.entityCd ? { entityCd: input.entityCd } : {}),
  };
}
